import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Inbox,
  Mail,
  FolderKanban,
  Sparkles,
  Mic,
  Calendar as CalendarIcon,
  Settings as SettingsIcon,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';

const nav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/drafts', label: 'Drafts', icon: Mail },
  { to: '/calendar', label: 'Calendar', icon: CalendarIcon },
  { to: '/projects', label: 'Projects', icon: FolderKanban },
  { to: '/briefs', label: 'Briefs', icon: Sparkles },
  { to: '/voice', label: 'Voice', icon: Mic },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Auto-close the drawer when the route changes (mobile nav tap).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground md:flex-row">
      {/* Mobile top bar */}
      <header className="flex items-center justify-between gap-3 border-b border-border bg-muted/60 px-4 py-2 md:hidden">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="-ml-2"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <p className="text-sm font-semibold tracking-tight">Command Centre</p>
        <div className="w-9" />
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden w-60 flex-col border-r border-border bg-muted/60 md:flex">
        <SidebarBody user={user} logout={logout} />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 120, damping: 14 }}
              className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-border bg-card md:hidden"
            >
              <div className="flex items-center justify-between px-4 py-3">
                <p className="text-sm font-semibold tracking-tight">Command Centre</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <SidebarBody user={user} logout={logout} compact />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 120, damping: 16 }}
          className="mx-auto max-w-6xl p-4 md:p-8"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}

function SidebarBody({
  user,
  logout,
  compact,
}: {
  user: { email: string } | null;
  logout: () => Promise<void> | void;
  compact?: boolean;
}) {
  return (
    <>
      {!compact && (
        <div className="px-5 pb-4 pt-6">
          <p className="text-sm font-semibold tracking-tight">Command Centre</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
      )}
      {compact && (
        <p className="px-5 pb-2 text-xs text-muted-foreground truncate">{user?.email}</p>
      )}
      <nav className="flex-1 space-y-0.5 px-3">
        {nav.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void logout()}
          className="w-full justify-start text-muted-foreground"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </>
  );
}
